/**
 * Distribution Hooks
 *
 * TanStack Query hooks for the VOC Rewards Phase 2 distribution system.
 * Follows patterns from hooks/useRewards.ts.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRewardsConfig,
  updateRewardsConfig,
  getCompoundEntries,
  createCompoundEntry,
  updateCompoundEntry,
  deleteCompoundEntry,
  getSwapEntries,
  createSwapEntry,
  updateSwapEntry,
  deleteSwapEntry,
  getRevenueEntries,
  createRevenueEntry,
  updateRevenueEntry,
  deleteRevenueEntry,
  getWalletExclusions,
  createWalletExclusion,
  updateWalletExclusion,
  deleteWalletExclusion,
  getAddressOverrides,
  createAddressOverride,
  updateAddressOverride,
  deleteAddressOverride,
  getRoyaltySummary,
  getRoyaltySales,
  getCoreumPrice,
  startDistributionPeriod,
  getDistributionPeriods,
  getActiveDistributionPeriod,
  endDistributionPeriod,
  distributeDistributionPeriod,
  getDistributionRecipients,
  exportDistributionCSV,
  submitDistributionClaim,
  getMyDistributionRewards,
  getPublicActivePeriodSummary,
  getPublicDistributionHistory,
  type RewardsConfig,
  type CompoundEntry,
  type SwapEntry,
  type RevenueEntry,
  type WalletExclusion,
  type RewardAddressOverride,
  type DistributionPeriod,
  type DistributionRecipient,
  type RoyaltySummary,
  type RoyaltySale,
  type ActivePeriodLiveStats,
  type HolderRewards,
} from '../api/distribution';

// ---- Config ----

export function useRewardsConfig(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'config', projectSlug],
    queryFn: () => getRewardsConfig(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 60 * 1000,
  });
}

export function useUpdateRewardsConfig(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (config: Partial<RewardsConfig>) => updateRewardsConfig(projectSlug, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'config', projectSlug] });
    },
  });
}

// ---- Compound Entries ----

export function useCompoundEntries(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'compounds', projectSlug],
    queryFn: () => getCompoundEntries(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useCreateCompoundEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { entry_date: string; amount: number; notes?: string }) =>
      createCompoundEntry(projectSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'compounds', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'active-period', projectSlug] });
    },
  });
}

export function useUpdateCompoundEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; entry_date?: string; amount?: number; notes?: string | null }) =>
      updateCompoundEntry(projectSlug, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'compounds', projectSlug] });
    },
  });
}

export function useDeleteCompoundEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCompoundEntry(projectSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'compounds', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'active-period', projectSlug] });
    },
  });
}

// ---- Swap Entries ----

export function useSwapEntries(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'swaps', projectSlug],
    queryFn: () => getSwapEntries(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useCreateSwapEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createSwapEntry>[1]) =>
      createSwapEntry(projectSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'swaps', projectSlug] });
    },
  });
}

export function useUpdateSwapEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<SwapEntry>) =>
      updateSwapEntry(projectSlug, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'swaps', projectSlug] });
    },
  });
}

export function useDeleteSwapEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSwapEntry(projectSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'swaps', projectSlug] });
    },
  });
}

// ---- Revenue Entries ----

export function useRevenueEntries(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'revenue', projectSlug],
    queryFn: () => getRevenueEntries(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useCreateRevenueEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createRevenueEntry>[1]) =>
      createRevenueEntry(projectSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'revenue', projectSlug] });
    },
  });
}

export function useUpdateRevenueEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<RevenueEntry>) =>
      updateRevenueEntry(projectSlug, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'revenue', projectSlug] });
    },
  });
}

export function useDeleteRevenueEntry(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRevenueEntry(projectSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'revenue', projectSlug] });
    },
  });
}

// ---- Wallet Exclusions ----

export function useWalletExclusions(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'exclusions', projectSlug],
    queryFn: () => getWalletExclusions(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useCreateWalletExclusion(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createWalletExclusion>[1]) =>
      createWalletExclusion(projectSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'exclusions', projectSlug] });
    },
  });
}

export function useUpdateWalletExclusion(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<WalletExclusion>) =>
      updateWalletExclusion(projectSlug, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'exclusions', projectSlug] });
    },
  });
}

export function useDeleteWalletExclusion(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWalletExclusion(projectSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'exclusions', projectSlug] });
    },
  });
}

// ---- Address Overrides ----

export function useAddressOverrides(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'overrides', projectSlug],
    queryFn: () => getAddressOverrides(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useCreateAddressOverride(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createAddressOverride>[1]) =>
      createAddressOverride(projectSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'overrides', projectSlug] });
    },
  });
}

export function useUpdateAddressOverride(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; reward_address?: string; label?: string | null }) =>
      updateAddressOverride(projectSlug, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'overrides', projectSlug] });
    },
  });
}

export function useDeleteAddressOverride(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAddressOverride(projectSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'overrides', projectSlug] });
    },
  });
}

// ---- Royalties ----

export function useRoyaltySummary(
  projectSlug: string | undefined,
  options?: { start_date?: string; end_date?: string }
) {
  return useQuery({
    queryKey: ['distribution', 'royalties', projectSlug, options?.start_date, options?.end_date],
    queryFn: () => getRoyaltySummary(projectSlug!, options),
    enabled: !!projectSlug,
    staleTime: 60 * 1000,
  });
}

export function useRoyaltySales(
  projectSlug: string | undefined,
  options?: { limit?: number; offset?: number; start_date?: string; end_date?: string }
) {
  return useQuery({
    queryKey: ['distribution', 'royalty-sales', projectSlug, options],
    queryFn: () => getRoyaltySales(projectSlug!, options),
    enabled: !!projectSlug,
    staleTime: 60 * 1000,
  });
}

// ---- COREUM Price ----

export function useCoreumPrice() {
  return useQuery({
    queryKey: ['coreum-price'],
    queryFn: getCoreumPrice,
    staleTime: 60 * 1000,
  });
}

// ---- Distribution Periods (Owner) ----

export function useDistributionPeriods(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'periods', projectSlug],
    queryFn: () => getDistributionPeriods(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useActiveDistributionPeriod(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'active-period', projectSlug],
    queryFn: () => getActiveDistributionPeriod(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function useStartDistributionPeriod(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      eligible_collection_ids: string[];
      pool_source?: string;
      distribution_mode?: string;
      notes?: string;
    }) => startDistributionPeriod(projectSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'periods', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'active-period', projectSlug] });
    },
  });
}

export function useEndDistributionPeriod(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, ...data }: {
      periodId: string;
      total_pool?: number;
      manual_rate_per_point?: number;
    }) => endDistributionPeriod(projectSlug, periodId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'periods', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'active-period', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'compounds', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'recipients'] });
    },
  });
}

export function useDistributeDistributionPeriod(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => distributeDistributionPeriod(projectSlug, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'periods', projectSlug] });
      queryClient.invalidateQueries({ queryKey: ['distribution', 'active-period', projectSlug] });
    },
  });
}

export function useDistributionRecipients(
  projectSlug: string | undefined,
  periodId: string | undefined,
  options?: { limit?: number; offset?: number; includeExcluded?: boolean }
) {
  return useQuery({
    queryKey: ['distribution', 'recipients', projectSlug, periodId, options],
    queryFn: () => getDistributionRecipients(projectSlug!, periodId!, options),
    enabled: !!projectSlug && !!periodId,
    staleTime: 30 * 1000,
  });
}

export function useExportDistributionCSV(projectSlug: string) {
  return useMutation({
    mutationFn: (periodId: string) => exportDistributionCSV(projectSlug, periodId),
  });
}

// ---- Holder Hooks ----

export function useSubmitDistributionClaim(projectSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => submitDistributionClaim(projectSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution', 'my-rewards', projectSlug] });
    },
  });
}

export function useMyDistributionRewards(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'my-rewards', projectSlug],
    queryFn: () => getMyDistributionRewards(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function usePublicActivePeriodSummary(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'public-active', projectSlug],
    queryFn: () => getPublicActivePeriodSummary(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}

export function usePublicDistributionHistory(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['distribution', 'public-history', projectSlug],
    queryFn: () => getPublicDistributionHistory(projectSlug!),
    enabled: !!projectSlug,
    staleTime: 30 * 1000,
  });
}
